import BaseObject from 'app/src/base/BaseObject';
import Decimal from 'app/src/data/base/Decimal';
import Payment from 'app/src/data/models/Payment';
import PaymentType from 'app/src/data/models/PaymentType';
import SalePaymentCollection from 'app/src/data/models/SalePaymentCollection';
import Sale from 'app/src/data/models/Sale';
import DepositPayment from 'app/src/payments/DepositPayment';
import _ from 'underscore';
import $ from 'jquery';

const _Events = {
  DEPOSITS_CHANGED: 'deposit:changed',
};

export default class DepositManager extends BaseObject {
  static get Events() {
    return _Events;
  }

  constructor(sale) {
    if (!(sale instanceof Sale)) {
      throw 'A sale must be specified on construction of a DepositManager';
    }

    super();

    this.clearDeposits();
    this.clearDepositRefunds();
    this.setInSaleDepositRefunds(sale);
  }

  /**
   * Retrieves the collection of deposit payments
   *
   * @returns {Array.<DepositPayment>}  The deposit payments
   */
  get deposits() {
    return _.values(this._deposits);
  }

  /**
   * Retrieves the collection of deposit refunds
   *
   * @returns {Array.<DepositPayment>}  The deposit refunds
   */
  get depositRefunds() {
    return _.values(this._depositRefunds);
  }

  /**
   * Retrieves whether or not the sale contains deposits
   *
   * @returns {boolean}   True if the sale contains deposits
   */
  get hasDeposits() {
    return this.deposits.length > 0;
  }

  /**
   * Retrieves the total amount of deposits
   */
  get depositTotal() {
    return this._totalDeposits(this.deposits);
  }

  /**
   * Retrieves the total amount of deposits refunds
   */
  get refundTotal() {
    return this._totalDeposits(this.depositRefunds).plus(this.getInSaleDepositRefundAmount());
  }

  /**
   * Adds a deposit to the deposit manager.  This is used for track which payments have
   * been allocated as deposits, how much deposit payments we have, etc.   The responsibility
   * of adding the payments to the sale is still the responsibility of the Sale Manager.
   *
   * @param {DepositPayment} deposit      The deposit payment to be added
   * @param {Boolean} isAccountPayment    true if the deposit is an account payment
   * @returns {Boolean}   True if the deposit was added
   */
  addDeposit(deposit, isAccountPayment = false) {
    let depositAdded = false;
    if (deposit instanceof DepositPayment) {
      depositAdded = true;
      this._deposits[deposit.model.get('remoteReference')] = deposit;
      this.trigger(_Events.DEPOSITS_CHANGED, isAccountPayment);
    } else {
      window.console.error('Payment was not a deposit refund.');
    }

    return depositAdded;
  }

  /**
   * Removes a deposit payment from the Deposit Manager.
   *
   * @param {DepositPayment} deposit      The deposit payment to remove.
   */
  removeDeposit(deposit) {
    if (deposit instanceof DepositPayment) {
      delete this._deposits[deposit.model.get('remoteReference')];
      this.trigger(_Events.DEPOSITS_CHANGED);
    } else {
      window.console.error('Payment to be removed was not a deposit.');
    }
  }

  /**
   * Removes and deposits associated with the specified payment.  The lookup is based on
   * a remote reference ID currently but could be easily expanded to also check for the
   * server-assigned payment ID.
   *
   * @param {Payment} payment     The payment for which a deposit is being removed
   */
  removeDepositForPayment(payment) {
    if (payment) {
      for (let depositReference in this._deposits) {
        if (
          this._deposits[depositReference].depositPayment.model.get('remoteReference') ===
          payment.model.get('remoteReference')
        ) {
          this.removeDeposit(this._deposits[depositReference]);
          this.trigger(_Events.DEPOSITS_CHANGED);
        }
      }
    }
  }

  /**
   * Attempts to retrieve the deposit associated with the specified payment.  The lookup is based on
   * a remote reference ID currently but could be easily expanded to also check for the
   * server-assigned payment ID.
   *
   * @param {Payment} payment     The payment for which a deposit is being removed
   */
  getDepositForPayment(payment) {
    let deposit = null;

    if (payment) {
      for (let depositReference in this._deposits) {
        if (
          this._deposits[depositReference].depositPayment.model.get('remoteReference') ===
          payment.model.get('remoteReference')
        ) {
          deposit = this._deposits[depositReference];
        }
      }
    }

    return deposit;
  }

  /**
   * Clears all deposits from the Deposit Manager.
   */
  clearDeposits() {
    this._deposits = {};
    this.trigger(_Events.DEPOSITS_CHANGED);
  }

  /**
   * Refund deposits are treated much like a typical pending payment but do not affect the
   * sale in any meaningful way.  As a result, we maintain a separate list of deposit refunds
   * that are added as payments when finishing the sale.
   *
   * @param {DepositPayment} depositRefund  The deposit refund payment.
   * @returns {boolean}   Returns true if the refund was successfully added to the sale
   */
  addDepositRefund(depositRefund) {
    let refundAdded = false;

    if (depositRefund instanceof DepositPayment) {
      this._depositRefunds[depositRefund.model.get('remoteReference')] = depositRefund;

      this.trigger(_Events.DEPOSITS_CHANGED);
      refundAdded = true;
    } else {
      window.console.error('Payment was not a deposit refund.');
    }

    return refundAdded;
  }

  /**
   * Remove a refund deposit from the Deposit Manager.
   *
   * @param {DepositPayment} depositRefund     The deposit refund to be removed
   */
  removeDepositRefund(depositRefund) {
    if (depositRefund instanceof DepositPayment) {
      delete this._depositRefunds[depositRefund.model.get('remoteReference')];
      this.trigger(_Events.DEPOSITS_CHANGED);
    } else {
      window.console.error('Payment to be removed was not a deposit.');
    }
  }

  /**
   * Removes all deposit refunds from the Deposit Manager.
   */
  clearDepositRefunds() {
    this._depositRefunds = {};
    this.trigger(_Events.DEPOSITS_CHANGED);
  }

  /**
   * Stores the deposit refunds that are currently in the sale.  Deposit refunds are defined as
   * credit account withdrawls that are in a sale.
   *
   * @param {Sale} sale   The sale model
   */
  setInSaleDepositRefunds(sale) {
    if (sale instanceof Sale) {
      let salePayments =
        sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

      this._inSaleDeposits = salePayments.filter(salePayment => {
        let paymentTypeRelation = salePayment.getRelation('PaymentType', PaymentType);

        return (
          paymentTypeRelation &&
          paymentTypeRelation.get('type') === PaymentType.Types.CREDIT_ACCOUNT &&
          !salePayment.get('archived') &&
          salePayment.get('amount').isGreaterThan(0)
        );
      });

      this.trigger(_Events.DEPOSITS_CHANGED);
    }
  }

  /**
   * Retrieves the total amount of deposit refunds that are currently in the sale.  The deposit
   * refunds are set using the setInSaleDepositRefunds method.
   *
   * @returns {Decimal}   The total amount of deposits registered with the deposit manager
   */
  getInSaleDepositRefundAmount() {
    let depositRefundAmount = new Decimal(0);

    this._inSaleDeposits.forEach(salePayment => {
      depositRefundAmount = depositRefundAmount.plus(salePayment.get('amount'));
    });

    return depositRefundAmount;
  }

  /**
   * Refunds the specified deposit payment.  The amount to be refunded is defined by the amount
   * specified by the deposit (i.e. it's not necessary for the deposit amount to cover the total
   * amount of it's associated payment).
   *
   * @param {DepositPayment} deposit  The deposit payment to be refunded
   */
  refund(deposit) {
    let deferred = $.Deferred();

    if (deposit instanceof DepositPayment) {
      // this only makes sense for payment service payments
      if (deposit.depositPayment.isServicePayment()) {
        deposit.depositPayment
          .refund(deposit.amount)
          .done(() => {
            deferred.resolve();
          })
          .fail(() => {
            deferred.reject();
          });
      } else {
        deferred.resolve();
      }
    } else {
      window.console.error('Payment to be refunded was not a deposit.');
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Give a list of deposits (either payments or refunds), calculates the total amount of
   * payments in the collection.
   *
   * @param {Array.<DepositPayment>} depositList     The list of of deposit payments
   * @private
   */
  _totalDeposits(depositList) {
    let amount = new Decimal(0);

    if (depositList && depositList.length) {
      _.each(depositList, deposit => {
        amount = amount.plus(deposit.amount.abs());
      });
    }

    return amount;
  }
}
